import { FC, useEffect, useState } from "react";
import styles from "./works-completed-table.module.scss";
import { useParams } from "react-router-dom";
import {
  useDeleteWorkCompletedMutation,
  useGetAllWorksCompletedQuery,
} from "./worksCompletedApi";
import { Button } from "../../../../ui/button/Button";
import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import { IWorkCompletedList } from "./IWorksComplited";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { Confirm } from "../../../../ui/confirm/confirm";
import { AddWorkCompleted } from "./addworkCompleted/addWorkCompleted";
import { min2HHMM } from "../../../../helpers/time";
import { EditWorkCompleted } from "./editworkCompleted/editWorkCompleted";

type RowProps = Omit<IWorkCompletedList, "work_id" | "land_category_code" | "contract_work_id" | "route_id"> & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({
  id,
  contract_work_name,
  time,
  route_name,
  fire,
  discharge_count,
  discharge_volume,
  influence_count,
  land_category_name,
  jumps,
  descent,
  handler,
}) => {
  return (
    <tr>
      <td>{contract_work_name}</td>
      <td>{min2HHMM(time)}</td>
      <td>{route_name}</td>
      <td>{fire}</td>
      <td>{discharge_count}</td>
      <td>{discharge_volume}</td>
      <td>{influence_count}</td>
      <td>{land_category_name}</td>
      <td>{jumps}</td>
      <td>{descent}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

const WorksCompletedTable: FC = () => {
  const { id } = useParams();
  const { data, isError, isFetching } = useGetAllWorksCompletedQuery(Number(id));
  const [
    deleteWork,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteWorkCompletedMutation();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);

  const create = () => {
    setIsAddOpen(true);
  };
  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true)
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Выполненные работы</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th1}>Вид работ</th>
            <th>Затраченное время</th>
            <th>№ маршрута</th>
            <th>Лесной пожар</th>
            <th className={styles.th2}>Количество сливов</th>
            <th className={styles.th2}>Объем сливов</th>
            <th className={styles.th2}>Воздействия</th>
            <th>Принадлежность лесов</th>
            <th className={styles.th2}>Прыжки</th>
            <th className={styles.th2}>Спуски</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((unit) => (
              <Row
                key={unit.id}
                id={unit.id}
                contract_work_name={unit.contract_work_name}
                time={unit.time}
                route_name={unit.route_name}
                fire={unit.fire}
                discharge_count={unit.discharge_count}
                discharge_volume={unit.discharge_volume}
                influence_count={unit.influence_count}
                land_category_name={unit.land_category_name}
                jumps={unit.jumps}
                descent={unit.descent}
                handler={rowHandler}
              />
            ))}
        </tbody>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && id && <AddWorkCompleted close={() => setIsAddOpen(false)} flight_work_id={Number(id)}/>} 
      {isEditOpen && id && ID && <EditWorkCompleted close={() => setIsEditOpen(false)} flight_work_id={Number(id)} works_completed_id={ID} />}
      {isError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deleteWork(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить запись из системы?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Работа удалена
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};

export default WorksCompletedTable;
